import { createFileRoute, Link } from "@tanstack/react-router";
import { FileSignature, Clock, Check, ArrowRight, ShieldCheck } from "lucide-react";
import {
  DOCUMENT_SERVICES,
  DOCUMENT_SERVICE_CATEGORIES,
} from "@/lib/document-services";
import { Disclaimer } from "@/components/site/Disclaimer";

export const Route = createFileRoute("/document-services/")({
  head: () => ({
    meta: [
      {
        title: "Automated Document Drafting from R400 | Nkadimeng Danny Legal",
      },
      {
        name: "description",
        content:
          "Order contracts, agreements, reviews, opinions and proposals online at fixed prices. Pay securely with PayFast and receive Word + PDF within 24 hours to 5 days.",
      },
      { property: "og:title", content: "Automated Document Drafting from R400" },
      {
        property: "og:description",
        content:
          "Fixed-price South African contracts, reviews, proposals and policies. Order online, pay with PayFast, receive Word + PDF.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: DocumentServicesPage,
});

function DocumentServicesPage() {
  return (
    <section className="container-narrow py-12">
      <header className="mx-auto max-w-3xl text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
          <FileSignature className="h-3.5 w-3.5" /> Fixed prices · PayFast checkout
        </span>
        <h1 className="mt-4 font-display text-4xl text-navy">
          Automated document drafting
        </h1>
        <p className="mt-3 text-muted-foreground">
          Choose the document you need, answer a few questions, and pay securely
          with PayFast. We draft it, quality-check it, and email you the Word and
          PDF versions within the stated turnaround.
        </p>
      </header>

      <div className="mt-10 space-y-12">
        {DOCUMENT_SERVICE_CATEGORIES.map((cat) => (
          <div key={cat}>
            <h2 className="font-display text-2xl text-navy">{cat}</h2>
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              {DOCUMENT_SERVICES.filter((s) => s.category === cat).map((s) => (
                <article
                  key={s.id}
                  className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft"
                >
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-display text-xl text-navy">{s.name}</h3>
                    <span className="whitespace-nowrap rounded-md bg-muted px-2.5 py-1 text-sm font-semibold text-navy">
                      R{s.priceZar}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{s.tagline}</p>
                  <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" /> {s.turnaround} · {s.bestFor}
                  </p>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    {s.includes.map((inc) => (
                      <li key={inc} className="flex gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                        <span>{inc}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/document-services/$serviceId"
                    params={{ serviceId: s.id }}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-navy px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"
                  >
                    Order for R{s.priceZar} <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        ))}
      </div>

      <p className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ShieldCheck className="h-4 w-4" /> All payments are processed by PayFast.
        We never see or store your card details.
      </p>

      <div className="mt-6">
        <Disclaimer />
      </div>
    </section>
  );
}
